"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Permissions = void 0;
var feature_areas_1 = require("./feature-areas");
/**
 * Main class that parses access control exsposes access to feature areas
 * mapped in `FEATURE_AREA_MAP
 */
var Permissions = /** @class */ (function () {
    /**
     * Permissions class constructor
     * @param accessControl data received from BE on init {@link AccessControl}
     * @param allEvalFn optional logic to run in BaseFeatureArea.evalPermission {@link AltPermissionEval}
     */
    function Permissions(accessControl, altEvalFn) {
        var _a, _b;
        this._featureAreas = {};
        this._appPermissions = this._getPermissions(accessControl.applicationAccessControl);
        var subMap = (_a = {}, _a[feature_areas_1.SUBSCRIPTION_ID] = accessControl.subscriptionAccessControl || null, _a);
        this._subPermissions = this._getPermissions(subMap);
        var orgMap = (_b = {}, _b[feature_areas_1.ORGANIZATION_ID] = accessControl.organizationAccessControl || null, _b);
        this._orgPermissions = this._getPermissions(orgMap);
        if (altEvalFn !== undefined) {
            this._altEvalFn = altEvalFn;
        }
    }
    /**
     * Entry point for evaluating app level permissions
     * @param id appId
     * @returns object with `entity` method for chaining as {@link EntityLink}
     */
    Permissions.prototype.$can = function (appId) {
        var _this = this;
        return {
            entity: function (entityName, entityOptions) {
                return _this._getEntity(appId, entityName, _this._appPermissions, entityOptions);
            }
        };
    };
    /**
     * Entry point for evaluating subscription level permission.
     * @returns boolean value of whether the user has subscription level permission
     */
    Permissions.prototype.$canSub = function () {
        var _this = this;
        return {
            entity: function (entityName, entityOptions) {
                return _this._getEntity(feature_areas_1.SUBSCRIPTION_ID, entityName, _this._subPermissions, entityOptions);
            }
        };
    };
    /**
     * Entry point for evaluating org admin level permission. Since it is just
     *   a boolean value, a simple return of _orgPermission suffices.
     * @returns boolean value of whether the user has org admin permissions
     */
    Permissions.prototype.$canOrg = function () {
        var _this = this;
        return {
            entity: function (entityName, entityOptions) {
                return _this._getEntity(feature_areas_1.ORGANIZATION_ID, entityName, _this._orgPermissions, entityOptions);
            }
        };
    };
    /**
     * Updates the local map with the instantiated FeatureAreaInstance
     * Runs feature area's `execute` method and returns
     * @param id appId (or possibly subId in the future) on which to access permissions
     * @param entityName key in FEATURE_AREA_MAP used to access desired feature area {@link FeatureAreaMap}
     * @param entityObject optional object (Guide, Page, etc.) for when it's necessary to check
     * @returns object with `action` method for chaining as {@link ActionLink}
     */
    Permissions.prototype._getEntity = function (id, entityName, permissionSet, entityOptions) {
        if (!this._featureAreas[entityName]) {
            var FeatureArea = feature_areas_1.FEATURE_AREA_MAP[entityName];
            // this handles when the feature area requested doesn't exist or hasn't been setup
            // could be an npm install error or just a typo, but we want to make noise so a dev knows
            if (!FeatureArea) {
                return this._handleFailedFeatureAreaLookup({ id: id, entity: entityName }, entityOptions);
            }
            this._featureAreas[entityName] = new FeatureArea(permissionSet, this._altEvalFn);
        }
        return this._featureAreas[entityName].execute({ id: id, entity: entityName }, entityOptions);
    };
    /**
     * Generate a map/tree structure from BE data that we use to eval permissions
     * @param accessControlSet - raw permission sets from BE keyed by appid/subid
     * @returns The generated map/tree structure as {@link EntityPermissionMap}
     */
    Permissions.prototype._getPermissions = function (accessControlSet) {
        var _this = this;
        return Object.keys(accessControlSet).reduce(function (permissions, key) {
            var accessControlItem = accessControlSet[key];
            if (accessControlItem !== null) {
                permissions[key] = accessControlItem.reduce(_this._buildEntityPermission, {});
            }
            return permissions;
        }, {});
    };
    /**
     * Return 'fake' chain to log properly log the error and evaluate to `false`.
     * @param currentPermission
     * @returns A fake ActionLink object for chaining
     */
    Permissions.prototype._handleFailedFeatureAreaLookup = function (currentPermission, entityOptions) {
        return {
            action: function (action) { return ({
                source: function (source) { return ({
                    target: function (target) {
                        var id = currentPermission.id, entity = currentPermission.entity;
                        // tslint:disable-next-line: no-console
                        console.error("[Pendo Permissions]: " + entity + " does not exist in FEATURE_AREA_MAP. Check for typos and verify your library version.", { id: id, entity: entity, action: action, source: source, target: target });
                        return false;
                    }
                }); }
            }); }
        };
    };
    /**
     * Builds/extends the entity permission - used in {@link _getPermissions} inside reduce
     * @param entity {@link EntityPermission} to extend
     * @param item {@link RawPermission} to incorporate into the {@link EntityPermission}
     *
     * @internal this contains core code for parsing permissions from the BE to be usable by this package. make changes carefully. and test. then test some more
     */
    Permissions.prototype._buildEntityPermission = function (entity, item) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (entity === void 0) { entity = {}; }
        var itemEnt = item.entity, action = item.action, source = item.source, target = item.target, fields = item.fields;
        var permission = { enabled: true, fields: fields };
        if (entity[itemEnt]) {
            if (entity[itemEnt][action]) {
                if (entity[itemEnt][action].source) {
                    entity[itemEnt][action].source[source] = permission;
                }
                else {
                    entity[itemEnt][action].source = (_a = {}, _a[source] = permission, _a);
                }
                if (entity[itemEnt][action].target) {
                    entity[itemEnt][action].target[target] = permission;
                }
                else {
                    entity[itemEnt][action].target = (_b = {}, _b[target] = permission, _b);
                }
            }
            else {
                entity[itemEnt][action] = {
                    source: (_c = {},
                        _c[source] = permission,
                        _c),
                    target: (_d = {},
                        _d[target] = permission,
                        _d)
                };
            }
        }
        else {
            entity[itemEnt] = (_e = {},
                _e[action] = {
                    source: (_f = {},
                        _f[source] = permission,
                        _f),
                    target: (_g = {},
                        _g[target] = permission,
                        _g)
                },
                _e);
        }
        return entity;
    };
    /**
     * Override permissions, change permissions after they have already been set
     * @param accessControl new permission sets
     * @param allEvalFn optional logic to run
     */
    Permissions.prototype.overridePermissions = function (accessControl, altEvalFn) {
        var _a, _b;
        this._featureAreas = {};
        this._appPermissions = this._getPermissions(accessControl.applicationAccessControl);
        var subMap = (_a = {}, _a[feature_areas_1.SUBSCRIPTION_ID] = accessControl.subscriptionAccessControl || null, _a);
        this._subPermissions = this._getPermissions(subMap);
        var orgMap = (_b = {}, _b[feature_areas_1.ORGANIZATION_ID] = accessControl.organizationAccessControl || null, _b);
        this._orgPermissions = this._getPermissions(orgMap);
        if (altEvalFn !== undefined) {
            this._altEvalFn = altEvalFn;
        }
        else {
            this._altEvalFn = function () { return null; };
        }
    };
    return Permissions;
}());
exports.Permissions = Permissions;
